package co.maytes.standupbot.dashboard.styles

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope

private val noPreference = CSSMediaQuery.Raw("(prefers-reduced-motion: no-preference)")
private val compact = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointMd}px)")
private val phone = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointSm}px)")

private const val ARROW_EASE = Motion.EASE_STANDARD

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.mediaRule(selector: String, query: CSSMediaQuery, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { cssRule(query, null) { Modifier.styleModifier(build) } }
}

@InitSilk
fun initMarketingStyles(ctx: InitSilkContext) {
    ctx.registerMarketingKeyframes()
    ctx.registerMarketingLayout()
    ctx.registerMarketingButtons()
    ctx.registerMarketingReveal()
    ctx.registerMarketingHero()
    ctx.registerMarketingStage()
    ctx.registerMarketingArt()
    ctx.registerMarketingPages()
    ctx.registerMarketingFooter()
    ctx.registerMarketingReducedMotion()
}

private fun InitSilkContext.registerMarketingKeyframes() {
    stylesheet.registerKeyframes("mk-rise") {
        from {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "translateY(${Marketing.ENTER_DISTANCE})")
            }
        }
        to {
            Modifier.styleModifier {
                property("opacity", "1")
                property("transform", "translateY(0)")
            }
        }
    }
    stylesheet.registerKeyframes("mk-reveal-up") {
        from {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "translateY(${Marketing.REVEAL_DISTANCE})")
            }
        }
        to {
            Modifier.styleModifier {
                property("opacity", "1")
                property("transform", "translateY(0)")
            }
        }
    }
    stylesheet.registerKeyframes("mk-scale-in") {
        from {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "translateY(${Marketing.REVEAL_DISTANCE}) scale(${Marketing.REVEAL_SCALE_FROM})")
            }
        }
        to {
            Modifier.styleModifier {
                property("opacity", "1")
                property("transform", "translateY(0) scale(1)")
            }
        }
    }
}

private fun InitSilkContext.registerMarketingLayout() {
    rule(".${Marketing.PAGE_CLASS}") {
        property("width", "100%")
        property("overflow-x", "clip")
    }
    mediaRule("html:has(.${Marketing.PAGE_CLASS})", noPreference) {
        property("scroll-behavior", "smooth")
    }
    rule(".mk-site") {
        property("font-family", Typography.FONT_SITE)
        property("-webkit-font-smoothing", "antialiased")
        property("font-feature-settings", "'ss01', 'cv11'")
    }
    rule(".mk-anchor") {
        property("scroll-margin-top", Marketing.ANCHOR_OFFSET)
    }
    rule(".mk-wrap") {
        property("width", "100%")
        property("max-width", "${Layout.wide}")
        property("margin-left", "auto")
        property("margin-right", "auto")
        property("padding-left", Marketing.GUTTER)
        property("padding-right", Marketing.GUTTER)
        property("box-sizing", "border-box")
    }
    rule(".mk-section") {
        property("position", "relative")
        property("padding-top", Marketing.SECTION_PAD)
        property("padding-bottom", Marketing.SECTION_PAD)
    }
    rule(".mk-band") {
        property("background-color", "${Palette.surfaceSubtle}")
    }
    rule(".mk-section-head") {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("text-align", "center")
        property("gap", "${Spacing.md}")
        property("margin-bottom", "${Spacing.xxl}")
    }

    rule(".mk-display") {
        property("margin", "0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", Marketing.FONT_DISPLAY)
        property("line-height", "1.02")
        property("letter-spacing", "-0.045em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-line") {
        property("display", "block")
    }
    rule(".mk-h2") {
        property("margin", "0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", Marketing.FONT_H2)
        property("line-height", "1.06")
        property("letter-spacing", "-0.04em")
        property("color", "${Palette.textPrimary}")
        property("max-width", Marketing.HEADING_MEASURE)
        property("text-wrap", "balance")
    }
    rule(".mk-lede") {
        property("margin", "0")
        property("font-size", Marketing.FONT_LEAD)
        property("line-height", "1.5")
        property("letter-spacing", "-0.011em")
        property("color", "${Palette.textSecondary}")
        property("max-width", Marketing.COPY_MEASURE)
        property("text-wrap", "pretty")
    }
    rule(".mk-body") {
        property("margin", "0")
        property("font-size", "${Typography.body.size}")
        property("line-height", "1.6")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-chip") {
        property("padding", "${Spacing.xxs} ${Spacing.sm}")
        property("border-radius", "${Radius.chip}")
        property("background-color", "${Palette.surfaceSubtle}")
        property("color", "${Palette.textSecondary}")
        property("font-size", "${Typography.caption.size}")
        property("font-weight", "${FontWeight.medium}")
    }
}

private fun InitSilkContext.registerMarketingButtons() {
    val focus: StyleScope.() -> Unit = {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", FocusRing.OFFSET)
    }
    rule(".mk-btn.mk-btn, .mk-btn.mk-btn:visited") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("gap", "${Spacing.xs}")
        property("min-height", Marketing.BUTTON_HEIGHT)
        property("padding", "0 ${Spacing.lg}")
        property("border-radius", "${Radius.pill}")
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("text-decoration", "none")
        property("cursor", "pointer")
        property("white-space", "nowrap")
        property(
            "transition",
            Motion.transition(Motion.DUR_BASE, "background-color", "color") +
                ", transform ${Motion.DUR_RELEASE} ${Motion.EASE_STANDARD}",
        )
    }
    rule(".mk-btn--sm.mk-btn--sm") {
        property("min-height", Marketing.BUTTON_HEIGHT_SM)
        property("padding", "0 ${Spacing.sm}")
        property("font-size", "${Typography.bodySmall.size}")
    }
    rule(".mk-btn--primary.mk-btn--primary, .mk-btn--primary.mk-btn--primary:visited") {
        property("background-color", "${Palette.primary}")
        property("color", "${Palette.onPrimary}")
    }
    rule(".mk-btn--ghost.mk-btn--ghost, .mk-btn--ghost.mk-btn--ghost:visited") {
        property("background-color", "${Palette.surfaceSubtle}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-btn--ghost.mk-btn--ghost") {
        property("border", "1px solid ${Marketing.EDGE}")
    }
    rule(".mk-btn--quiet.mk-btn--quiet, .mk-btn--quiet.mk-btn--quiet:visited") {
        property("background-color", "transparent")
        property("color", "${Palette.textPrimary}")
        property("padding", "0 ${Spacing.sm}")
    }
    rule(".mk-btn--invert.mk-btn--invert, .mk-btn--invert.mk-btn--invert:visited") {
        property("background-color", Marketing.CLOSE_WHITE)
        property("color", Marketing.CLOSE_BG)
    }
    rule(".mk-btn--invert-ghost.mk-btn--invert-ghost, .mk-btn--invert-ghost.mk-btn--invert-ghost:visited") {
        property("background-color", Marketing.CLOSE_SURFACE)
        property("color", Marketing.CLOSE_WHITE)
    }
    rule(".mk-btn-arrow") {
        property("display", "inline-block")
        property("transition", Motion.transitionWith(Motion.DUR_BASE, ARROW_EASE, "transform"))
    }
    mediaRule(".mk-btn:hover .mk-btn-arrow", HoverFinePointerMedia) {
        property("transform", "translateX(${Marketing.ARROW_NUDGE})")
    }
    mediaRule(".mk-btn--primary.mk-btn--primary:hover", HoverFinePointerMedia) {
        property("background-color", "${Palette.primaryHover}")
    }
    mediaRule(".mk-btn--ghost.mk-btn--ghost:hover", HoverFinePointerMedia) {
        property("background-color", "${Palette.surfaceHover}")
    }
    mediaRule(".mk-btn--quiet.mk-btn--quiet:hover", HoverFinePointerMedia) {
        property("background-color", "${Palette.surfaceSubtle}")
    }
    mediaRule(".mk-btn--invert.mk-btn--invert:hover", HoverFinePointerMedia) {
        property("background-color", Marketing.CLOSE_TEXT)
    }
    mediaRule(".mk-btn--invert-ghost.mk-btn--invert-ghost:hover", HoverFinePointerMedia) {
        property("background-color", Marketing.CLOSE_SURFACE_HOVER)
    }
    rule(".mk-btn.mk-btn:active") {
        property("transform", "scale(${Motion.BUTTON_PRESS_SCALE})")
        property("transition-duration", Motion.DUR_PRESS)
    }
    rule(".mk-btn:focus-visible, .mk-textbtn:focus-visible, .mk-quiet:focus-visible", focus)

    rule(".mk-quiet.mk-quiet, .mk-quiet.mk-quiet:visited") {
        property("color", "${Palette.textSecondary}")
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.medium}")
        property("text-decoration", "none")
        property("transition", Motion.transition(Motion.DUR_BASE, "color"))
    }
    mediaRule(".mk-quiet.mk-quiet:hover", HoverFinePointerMedia) {
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-textbtn") {
        property("appearance", "none")
        property("border", "0")
        property("background", "transparent")
        property("padding", "${Spacing.xxs} ${Spacing.xs}")
        property("border-radius", "${Radius.control}")
        property("font-family", "inherit")
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textSecondary}")
        property("cursor", "pointer")
        property(
            "transition",
            Motion.transition(Motion.DUR_BASE, "opacity", "color") +
                ", transform ${Motion.DUR_RELEASE} ${Motion.EASE_STANDARD}",
        )
    }
    rule(".mk-textbtn:disabled") {
        property("opacity", "0")
        property("pointer-events", "none")
    }
    mediaRule(".mk-textbtn:hover", HoverFinePointerMedia) {
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-textbtn:active") {
        property("transform", "scale(${Motion.BUTTON_PRESS_SCALE})")
    }
}

private fun InitSilkContext.registerMarketingReveal() {
    rule(".${Marketing.REVEAL_CLASS}:not(.${Marketing.REVEAL_IN_CLASS})") {
        property("opacity", "0")
    }
    rule(".${Marketing.REVEAL_CLASS}.${Marketing.REVEAL_IN_CLASS}") {
        property("animation", "mk-reveal-up ${Marketing.DUR_REVEAL} ${Motion.EASE_EMPHASIS} backwards")
        property(
            "animation-delay",
            "calc(min(var(${Marketing.INDEX_VAR}, 0), ${Marketing.REVEAL_STEP_CAP}) * ${Marketing.REVEAL_STEP_MS}ms)",
        )
    }
    rule(".${Marketing.REVEAL_SCALE_CLASS}.${Marketing.REVEAL_IN_CLASS}") {
        property("animation-name", "mk-scale-in")
    }
}

private fun InitSilkContext.registerMarketingHero() {
    rule(".mk-hero") {
        property("position", "relative")
        property("padding-top", "calc(${Marketing.HERO_TOP_PAD} + ${HomeTokens.NAV_CLEARANCE})")
        property("padding-bottom", Marketing.SECTION_PAD)
    }
    rule(".mk-enter") {
        property("animation", "mk-rise ${Marketing.DUR_HERO_ENTER} ${Motion.EASE_EMPHASIS} backwards")
        property("animation-delay", "calc(var(${Marketing.INDEX_VAR}, 0) * ${Marketing.HERO_STEP_MS}ms)")
    }
    rule("html.sb-boot .mk-enter, html.sb-boot .mk-site .sb-enter-fade-up, html.sb-boot .mk-site .sb-enter-fade") {
        property("animation", "none")
    }
    rule(".mk-actions") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
    }
}

private fun InitSilkContext.registerMarketingStage() {
    rule(".mk-stage") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.md}")
        property("min-width", "0")
    }
    rule(".mk-steps") {
        property("display", "grid")
        property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
        property("gap", "${Spacing.xs}")
    }
    mediaRule(".mk-steps", phone) {
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
    }
    rule(".mk-step") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.xs}")
        property("padding", "${Spacing.xs} ${Spacing.sm}")
        property("border-radius", "${Radius.control}")
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textTertiary}")
        property("transition", Motion.transition(Motion.DUR_BASE, "background-color", "color"))
    }
    rule(".mk-step-dot") {
        property("flex", "none")
        property("width", Marketing.STEP_DOT)
        property("height", Marketing.STEP_DOT)
        property("border-radius", "${Radius.pill}")
        property("background-color", "${Palette.border}")
        property("transition", Motion.transition(Motion.DUR_BASE, "background-color"))
    }
    rule(".mk-step--active") {
        property("background-color", "${Palette.background}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-step--active .mk-step-dot") {
        property("background-color", "${Palette.textPrimary}")
    }
    rule(".mk-step--done") {
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-step--done .mk-step-dot") {
        property("background-color", "${Palette.textSecondary}")
    }

    rule(".mk-window-stack") {
        property("display", "grid")
    }
    rule(".mk-window-stack > *") {
        property("grid-area", "1 / 1")
        property("min-width", "0")
    }
    rule(".mk-window-sizer") {
        property("visibility", "hidden")
        property("pointer-events", "none")
    }
    rule(".mk-window .sb-slack") {
        property("box-shadow", HomeTokens.SHOWCASE_SHADOW)
    }
    rule(".mk-window") {
        property("transition", Motion.transitionWith(Marketing.DUR_STAGE_FADE, Motion.EASE_EMPHASIS, "opacity", "transform"))
    }
    rule(".mk-window--pending") {
        property("opacity", "0.35")
        property("transform", "translateY(${Marketing.WINDOW_PENDING_SHIFT})")
    }
    rule(".mk-window .sb-slack-divider, .mk-window .sb-slack-actions") {
        property("transition", Motion.transition(Marketing.DUR_STAGE_FADE, "opacity"))
    }
    rule(".mk-window--typing .sb-slack-divider, .mk-window--typing .sb-slack-actions") {
        property("opacity", "0")
        property("pointer-events", "none")
    }
    rule(".mk-window .sb-slack-btn") {
        property("cursor", "pointer")
        property("transition", Motion.transition(Motion.DUR_FAST, "filter") + ", transform ${Motion.DUR_PRESS} ${Motion.EASE_STANDARD}")
    }
    mediaRule(".mk-window .sb-slack-btn:hover", HoverFinePointerMedia) {
        property("filter", "brightness(0.95)")
    }
    rule(".mk-window .sb-slack-btn:active, .mk-window .sb-slack-btn--pressed") {
        property("transform", "scale(${Marketing.ACK_PRESS_SCALE})")
        property("filter", "brightness(0.9)")
    }
    rule(".mk-stage-foot") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", "${Spacing.sm}")
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textTertiary}")
    }
}

private fun InitSilkContext.registerMarketingFooter() {
    rule(".mk-footer") {
        property("width", "100%")
        property("background-color", "${Palette.surfaceSubtle}")
        property("padding-top", Marketing.SECTION_PAD_TIGHT)
        property("overflow", "hidden")
    }
    rule(".mk-footer-cols") {
        property("display", "grid")
        property("grid-template-columns", "minmax(0, 1.6fr) repeat(3, minmax(0, 1fr))")
        property("gap", "${Spacing.xl}")
        property("align-items", "start")
    }
    mediaRule(".mk-footer-cols", compact) {
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
    }
    mediaRule(".mk-footer-brand", compact) {
        property("grid-column", "1 / -1")
    }
    rule(".mk-footer-col") {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "flex-start")
        property("gap", "${Spacing.sm}")
    }
    rule(".mk-footer-brand") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.sm}")
        property("max-width", "20rem")
    }
    rule(".mk-footer-name") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.xs}")
        property("font-weight", "${FontWeight.semibold}")
        property("letter-spacing", "-0.02em")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-footer-logo") {
        property("width", "1.5rem")
        property("height", "1.5rem")
    }
    rule(".mk-footer-blurb") {
        property("margin", "0")
        property("font-size", "${Typography.bodySmall.size}")
        property("line-height", "1.55")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-footer-link.mk-footer-link, .mk-footer-link.mk-footer-link:visited") {
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
        property("text-decoration", "none")
        property("transition", Motion.transition(Motion.DUR_BASE, "color"))
    }
    mediaRule(".mk-footer-link.mk-footer-link:hover", HoverFinePointerMedia) {
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-footer-heading") {
        property("margin", "0")
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-footer-mark") {
        property("margin", "${Spacing.xxl} 0 0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", Marketing.FONT_MARK)
        property("line-height", "0.8")
        property("letter-spacing", "-0.05em")
        property("color", "${Palette.border}")
        property("white-space", "nowrap")
        property("user-select", "none")
    }
    rule(".mk-footer-base") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("justify-content", "space-between")
        property("gap", "${Spacing.sm}")
        property("margin-top", "${Spacing.lg}")
        property("padding-bottom", "${Spacing.lg}")
        property("font-size", "${Typography.caption.size}")
        property("color", "${Palette.textTertiary}")
    }
}

private fun InitSilkContext.registerMarketingReducedMotion() {
    fadeInsteadOfMove(".mk-enter", "sb-enter-fade")
    fadeInsteadOfMove(".${Marketing.REVEAL_CLASS}.${Marketing.REVEAL_IN_CLASS}", "sb-enter-fade")
    reducedMotionRule(".${Marketing.REVEAL_CLASS}:not(.${Marketing.REVEAL_IN_CLASS})") {
        property("opacity", "1")
    }
    reducedMotionRule(".mk-window--pending") {
        property("transform", "none !important")
        property("opacity", "1")
    }
    reducedMotionRule(".mk-btn, .mk-btn-arrow") {
        property("transform", "none !important")
    }
}
